import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { usePlayback } from "../../context/PlaybackContext";
import { useSimulationSessionData } from '../../hooks/useSimulationSessionData';
import { Button } from "react-bootstrap";
import { HelpIconButton } from "./HelpIconButton";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
  ReferenceDot
} from 'recharts';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const ActiveVehiclePlot = (props: {
  toggleShowActiveVehiclePlot: any,
  vehicleId?: any | null
}) => {
  const { playbackOffset, setPlaybackSession, clearPlayback } = usePlayback();

  const {
    simulationSessionMap,
    activeCountData,
   } = useSimulationSessionData();
  const {activeCountMap, sortedCountKeys} = activeCountData;

  const [touchStartDist, setTouchStartDist] = useState<number | null>(null);
  const [mouseX, setMouseX] = useState<number | null>(null);
  const [mouseY, setMouseY] = useState<number | null>(null);

  const [msXPoint, setMsXPoint] = useState<number | null>(null);
  const [allowResize, setAllowResize] = useState(true);

  const chartRef = React.useRef<HTMLDivElement>(null);

  // Calculate full available time window from session data
  const [availableStart, availableEnd] = useMemo(() => {
    const now = Date.now();
    let minStart = Infinity;
    let maxEnd = now;

    if (simulationSessionMap && simulationSessionMap.size() > 0) {
      simulationSessionMap.forEach((s: any) => {
        if (s.start) {
          const startMs = typeof s.start === 'number' ? s.start : new Date(s.start).getTime();
          if (!isNaN(startMs) && startMs < minStart) {
            minStart = startMs;
          }
        }
        if (s.end) {
          const endMs = typeof s.end === 'number' ? s.end : new Date(s.end).getTime();
          if (!isNaN(endMs) && endMs > maxEnd) {
            maxEnd = endMs;
          }
        }
      });
    }

    if (sortedCountKeys && sortedCountKeys.length > 0) {
      const firstKey = sortedCountKeys[0];
      const lastKey = sortedCountKeys[sortedCountKeys.length - 1];
      if (firstKey < minStart) minStart = firstKey;
      if (lastKey > maxEnd) maxEnd = lastKey;
    }

    if (minStart === Infinity) {
      minStart = now - ONE_DAY_MS;
    }

    let start = minStart;
    const end = maxEnd;

    // Enforce minimum window of 1 day if runs are all within the last day or no valid runs
    if (end - start < ONE_DAY_MS) {
      start = end - ONE_DAY_MS;
    }

    return [start, end];
  }, [simulationSessionMap, sortedCountKeys]);

  const [domain, setDomain] = useState<[number, number]>([availableStart, availableEnd]);

  // Sync default domain when availableStart or availableEnd changes, if not scoped to a single vehicleId and not zoomed manually
  useEffect(() => {
    if (!props.vehicleId && allowResize) {
      setDomain([availableStart, availableEnd]);
    }
  }, [availableStart, availableEnd, props.vehicleId, allowResize]);

  useEffect(() => {
    if(!chartRef || !chartRef.current || !mouseX || !mouseY) return;

    // Get the bounding box of the chart container
    const rectChart = chartRef.current.getBoundingClientRect();
    const [currentStart, currentEnd] = domain;

    const chartMargins = {
      left: 65,
      right: 5,
      top: 5,
      bottom: 40,
    };

    // If the mouse is outside the LineChart area, the msXPoint is invalid.
    if((mouseX < chartMargins.left + rectChart.left) ||
       (mouseX > rectChart.right - chartMargins.right) ||
       (mouseY < rectChart.top + chartMargins.top) ||
       (mouseY > rectChart.bottom - chartMargins.bottom)) {
      setMsXPoint(null);
      return;
    }

    // Define the chart margins (Recharts defaults + Y-Axis width)
    const chartWidth = rectChart.width - chartMargins.left - chartMargins.right;

    // Calculate where the mouse is relative to the start of the line area
    const xInChart = mouseX - chartMargins.left - rectChart.x;

    const timeSpan = currentEnd - currentStart;

    // Calculate the percentage across the X-axis (clamped between 0 and 1)
    const percentage = Math.max(0, Math.min(1, xInChart / chartWidth));

    // Map that percentage to your current time domain
    const exactMsTime = currentStart + (timeSpan * percentage);

    setMsXPoint(exactMsTime);
  }, [mouseX, mouseY, domain]);

  // Generate Chart Data
  const chartData = useMemo(() => {
    if (simulationSessionMap.size() === 0) return;

    const data: any[] = [];

    sortedCountKeys.forEach((key) => {
      const dateObj = new Date(key);
      const activeCount = activeCountMap.get(key);

      data.push({
        time: dateObj,
        // X-Axis usually looks better with just the date or hour
        displayTime: dateObj.toLocaleDateString([], { month: 'short', day: 'numeric' }),
        // Create a detailed string specifically for the tooltip
        fullLocalTime: dateObj.toLocaleString([], {
          timeZone: 'UTC',
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
          month: 'short',
          day: 'numeric'
        }),
        msTime: dateObj.getTime(),
        count: activeCount
      });
    });

    // If a vehicle ID was provided, restrict the window to that vehicle's timespan.
    if (props.vehicleId) {
      const driverSession = simulationSessionMap.get(props.vehicleId);
      if (driverSession) {
        if (allowResize) {
          setAllowResize(false);
          const startDate = new Date(driverSession.start);
          const endDate = driverSession.end ? new Date(driverSession.end) : new Date();
          setDomain([startDate.getTime(), endDate.getTime()]);
        }
      }
      else {
        console.error("Didn't find session for vehicle ID", props.vehicleId);
      }
    }

    return data;

  }, [sortedCountKeys, activeCountMap, simulationSessionMap, props.vehicleId, allowResize]);

  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    if (!chartRef.current) return;
    const updateWidth = () => {
      if (chartRef.current) {
        setContainerWidth(chartRef.current.clientWidth);
      }
    };
    updateWidth();

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });
    resizeObserver.observe(chartRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const maxTicks = useMemo(() => {
    const width = containerWidth || (typeof window !== 'undefined' ? Math.min(window.innerWidth - 40, 800) : 600);
    // Y-axis + margins take approx 70px
    const plotWidth = Math.max(150, width - 70);
    // Target min spacing per tick label (in px) to prevent label crowding/overlap
    const minSpacing = 70;
    return Math.max(2, Math.floor(plotWidth / minSpacing));
  }, [containerWidth]);

  const getTicks = useCallback((start: number, end: number, targetMaxTicks: number) => {
    const span = end - start;
    if (span <= 0 || targetMaxTicks < 2) return undefined;

    const spanDays = span / ONE_DAY_MS;
    const ticks: number[] = [];

    if (spanDays >= 2) {
      // Multi-day view: align ticks to UTC midnight
      const idealStepDays = spanDays / targetMaxTicks;
      const NICE_DAY_STEPS = [1, 2, 3, 5, 7, 10, 14, 15, 21, 30, 45, 60, 90, 120, 180, 365];
      const stepDays = NICE_DAY_STEPS.find(s => s >= idealStepDays) || NICE_DAY_STEPS[NICE_DAY_STEPS.length - 1];

      let current = new Date(start);
      // Normalize to UTC midnight at or after start
      current.setUTCHours(24, 0, 0, 0);

      while (current.getTime() <= end) {
        ticks.push(current.getTime());
        current.setUTCDate(current.getUTCDate() + stepDays);
      }
    } else {
      // Sub-2-day view: align ticks to clean hour / minute intervals
      const NICE_MS_STEPS = [
        1 * 60 * 1000,        // 1 min
        2 * 60 * 1000,        // 2 min
        5 * 60 * 1000,        // 5 min
        10 * 60 * 1000,       // 10 min
        15 * 60 * 1000,       // 15 min
        30 * 60 * 1000,       // 30 min
        1 * 3600 * 1000,      // 1 hour
        2 * 3600 * 1000,      // 2 hours
        3 * 3600 * 1000,      // 3 hours
        4 * 3600 * 1000,      // 4 hours
        6 * 3600 * 1000,      // 6 hours
        12 * 3600 * 1000,     // 12 hours
        24 * 3600 * 1000,     // 24 hours
      ];
      const idealStepMs = span / targetMaxTicks;
      const stepMs = NICE_MS_STEPS.find(s => s >= idealStepMs) || NICE_MS_STEPS[NICE_MS_STEPS.length - 1];

      let currentMs = Math.ceil(start / stepMs) * stepMs;
      while (currentMs <= end) {
        ticks.push(currentMs);
        currentMs += stepMs;
      }
    }

    return ticks.length > 0 ? ticks : undefined;
  }, []);

  const xAxisTicks = useMemo(() => {
    return getTicks(domain[0], domain[1], maxTicks);
  }, [domain, maxTicks, getTicks]);

  const isMultiDay = useMemo(() => {
    const startDate = new Date(domain[0]);
    const endDate = new Date(domain[1]);

    // Compare UTC components to ignore local timezone offsets
    return (
      startDate.getUTCFullYear() !== endDate.getUTCFullYear() ||
      startDate.getUTCMonth() !== endDate.getUTCMonth() ||
      startDate.getUTCDate() !== endDate.getUTCDate()
    );
  }, [domain]);

  const isRightOfCenter = useMemo(() => {
    if (!chartRef.current || !mouseX || !mouseY) return false;
    const rect = chartRef.current.getBoundingClientRect();
    return (mouseX - rect.left) > (rect.width / 2);
  }, [mouseX, mouseY]);

  const hoveredCount = useMemo(() => {
    if (!msXPoint || !chartData || chartData.length === 0) return 0;
    // Find the last data point that happened before or exactly at the mouse point
    const point = chartData.reduce((prev, curr) =>
      (curr.msTime <= msXPoint ? curr : prev),
      chartData[0]
    );
    return point?.count || 0;
  }, [msXPoint, chartData]);

  const hoveredTimestamp = useMemo(() => {
    if (!msXPoint || !chartData || chartData.length === 0) return undefined;
    const span = domain[1] - domain[0];

    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'UTC',
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    };
    const date = new Date(msXPoint);
    if (span < 60 * 60 * 1000) {
      return `${date.toLocaleTimeString([], { ...options, second: '2-digit' })}Z`;
    }
    return `${date.toLocaleTimeString([], options)}Z`;

  }, [domain, msXPoint, chartData]);

  // Extracted zoom logic for reuse between Wheel and Touch
  const performZoom = useCallback((isZoomIn: boolean, anchor: number) => {
    setAllowResize(false);
    const [currentStart, currentEnd] = domain;
    const zoomFactor = 0.1;

    if (isZoomIn) {
      let newStart = currentStart + (anchor - currentStart) * zoomFactor;
      let newEnd = currentEnd - (currentEnd - anchor) * zoomFactor;

      newStart = Math.max(availableStart, newStart);
      newEnd = Math.min(availableEnd, newEnd);

      // Prevent zooming in too far (e.g., closer than 1 minute)
      if (newEnd - newStart > 60000) {
        setDomain([newStart, newEnd]);
      }
    } else {
      let newStart = currentStart - (anchor - currentStart) * zoomFactor;
      let newEnd = currentEnd + (currentEnd - anchor) * zoomFactor;

      newStart = Math.max(availableStart, newStart);
      newEnd = Math.min(availableEnd, newEnd);

      setDomain([newStart, newEnd]);
    }
  }, [domain, availableStart, availableEnd]);

  // Helper to format the title based on the viewable span
  const getDynamicTitle = (start: number, end: number) => {
    const span = end - start;
    const startDate = new Date(start);
    const endDate = new Date(end);

  const options: Intl.DateTimeFormatOptions = {
      timeZone: 'UTC',
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    };

    if (span > 24 * 60 * 60 * 1000 * 2) {
      return `Vehicle Activity: ${startDate.toLocaleDateString([], options)} to ${endDate.toLocaleDateString([], options)}`;
    } else if (span > 60 * 60 * 1000) {
      if (isMultiDay) {
        return `Vehicle Activity: ${startDate.toLocaleTimeString([], { ...options, hour: '2-digit', minute: '2-digit' })}Z to ${endDate.toLocaleTimeString([], { ...options, hour: '2-digit', minute: '2-digit' })}Z`;
      } else {
        return `Vehicle Activity: ${startDate.toLocaleTimeString([], { ...options, hour: '2-digit', minute: '2-digit' })}Z to ${endDate.toLocaleTimeString([], { ...options, hour: '2-digit', minute: '2-digit' })}Z`;
      }
    }
    return `Vehicle Activity: ${startDate.toLocaleString([], { ...options, second: '2-digit' })}Z to ${endDate.toLocaleString([], { ...options, second: '2-digit' })}Z`;
  };

  // Helper for X-Axis ticks
  const formatXAxis = (unixTime: number, domain: [number, number]) => {
    const span = domain[1] - domain[0];
    const date = new Date(unixTime);

    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'UTC',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    };

    if (span > 24 * 60 * 60 * 1000 * 2) {
      return date.toLocaleDateString([], {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
      });
    } else if (span > 60 * 60 * 1000) {
      return `${date.toLocaleTimeString([], options)}Z`;
    }
    return `${date.toLocaleTimeString([], { ...options, second: '2-digit' })}Z`;
  };

  const handleChartClick = (state: any) => {
    if (!msXPoint) return;

    const strExactMsTime = new Date(msXPoint).toISOString();

    // Update the playback session
    setPlaybackSession(strExactMsTime);

    props.toggleShowActiveVehiclePlot();
  };

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (msXPoint === null) return;

    performZoom(e.deltaY < 0, msXPoint);
  }, [performZoom, msXPoint]);

  const getTouchDist = (e: React.TouchEvent) => {
    if (e.touches.length !== 2) return null;
    const dx = e.touches[0].pageX - e.touches[1].pageX;
    const dy = e.touches[0].pageY - e.touches[1].pageY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dist = getTouchDist(e);
      setTouchStartDist(dist);
      // Use the midpoint between two fingers as the zoom anchor
      setMouseX((e.touches[0].pageX + e.touches[1].pageX) / 2);
      setMouseY((e.touches[0].pageY + e.touches[1].pageY) / 2);
    }
    if ((e.touches.length === 1) && !touchStartDist) {
      if (!msXPoint) return;

      const strExactMsTime = new Date(msXPoint).toISOString();

      // Update the playback session
      setPlaybackSession(strExactMsTime);

      console.log("Warping to calculated time:", strExactMsTime);
      props.toggleShowActiveVehiclePlot();
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && touchStartDist !== null) {
      const currentDist = getTouchDist(e);
      if (!currentDist) return;
      if (!msXPoint) return;

      const zoomThreshold = 2; // Pixels
      const diff = currentDist - touchStartDist;

      if (Math.abs(diff) > zoomThreshold) {
        const isZoomIn = diff > 0;

        performZoom(isZoomIn, msXPoint);

        // Update starting distance to allow continuous zooming
        setTouchStartDist(currentDist);
      }
    }
  };

  const currentPlaybackTime = Date.now() - playbackOffset;

  return (
    <div
      className="active-vehicle-plot-container"
      onWheel={handleWheel}
      onMouseMove={(state) => {
        if (state && state.clientX) {
          setMouseX(state.clientX);
          setMouseY(state.clientY);
        }
      }}
      onMouseLeave={() => {
        setMouseX(null);
        setMouseY(null);
        setMsXPoint(null);
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={() => {
        setMouseX(null);
        setMouseY(null);
        setMsXPoint(null);
        setTouchStartDist(null);
      }}
      style={{
        touchAction: 'none',
        position: 'relative',
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(2px)',
        padding: containerWidth && containerWidth < 480 ? '12px 8px' : '20px',
        borderRadius: '8px',
        zIndex: 1000,
        paddingBottom: '20px',
        width: '94%',
        margin: '20px auto',
        boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0 d-flex align-items-center">
          {getDynamicTitle(domain[0], domain[1])}
          <HelpIconButton topicId="active-vehicle-plot" title="Active Vehicle Plot Help" className="ms-2" />
        </h5>
      </div>

      <div
        ref={chartRef}
        style={{ width: '100%', height: 300 }}
      >
        <ResponsiveContainer>
          <LineChart
            data={chartData}
            onMouseDown={handleChartClick}
            style={{ cursor: 'crosshair' }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              strokeOpacity={0.8}
              fill="rgba(0, 0, 0, 0.07)" // Light gray fill for the interior
              fillOpacity={1}
            />

            <XAxis
              dataKey="msTime"
              type="number"
              allowDataOverflow={true}
              domain={domain}
              ticks={xAxisTicks}
              interval="preserveStartEnd"
              minTickGap={15}
              tick={{ fontSize: 11 }}
              tickFormatter={(unixTime) => formatXAxis(unixTime, domain)}
            />
            <YAxis />

            {/* Sweeping Line: Only show if it falls within the current visible domain */}
            {currentPlaybackTime >= domain[0] && currentPlaybackTime <= domain[1] && (
              <ReferenceLine
                x={playbackOffset === 0 ? domain[1] : currentPlaybackTime}
                stroke="red"
                strokeWidth={2}
                zIndex={1001}
                label={{ position: 'top', value: 'Live', fill: 'red', fontSize: 10 }}
              />
            )}
            {/* Red Dot and vertical line following the mouse exactly on the line */}
            {msXPoint && chartData && (
              <>
                <ReferenceDot
                  x={msXPoint}
                  // Logic to find the Y value: find the last data point that happened BEFORE the mouse X
                  y={chartData.reduce((prev, curr) => (curr.msTime <= msXPoint ? curr : prev), chartData[0])?.count}
                  r={4}
                  fill="red"
                  stroke="none"
                />
                <ReferenceLine
                  x={msXPoint}
                  stroke="rgba(0,0,0,0.1)"
                  strokeDasharray="3 3"
                />
              </>
            )}
            <Tooltip active={false}/> {/* Disable default snapping tooltip */}
            <Line
              type="stepAfter"
              dataKey="count"
              stroke="#8884d8"
              fill="#8884d8"
              strokeWidth={2}
              fillOpacity={0.8}
              dot={false}
              activeDot={false}
              connectNulls={true}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
        {/* Tooltip tied to the ReferencePoint at the cursor X position */}
        {msXPoint && mouseX && mouseY && (
          <div
            style={{
              position: 'fixed',
              // Dynamic anchoring logic:
              left: isRightOfCenter ? 'auto' : mouseX + 15,
              right: isRightOfCenter ? (window.innerWidth - mouseX) + 15 : 'auto',
              top: '50%',
              pointerEvents: 'none',
              background: 'white',
              border: '1px solid #ccc',
              padding: '8px',
              borderRadius: '4px',
              zIndex: 1002,
              fontSize: '12px',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}
          >
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              {hoveredTimestamp}
            </div>
            <div style={{ color: '#8884d8' }}>
              Active Vehicles: {hoveredCount}
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 d-flex justify-content-end gap-2">
        {playbackOffset !== 0 && (
          <Button variant="success" onClick={clearPlayback}>Return to Live</Button>
        )}
        <Button variant="secondary" onClick={props.toggleShowActiveVehiclePlot}>Close</Button>
      </div>
    </div>
  );
};